// src/hooks/useOrderDraft.js
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const DRAFT_STORAGE_KEY = 'order_draft';
const AUTO_SAVE_INTERVAL = 30000; // 30 secondi

export const useOrderDraft = () => {
  const [customer, setCustomer] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('contrassegno');
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
  const [autoSaveInterval, setAutoSaveInterval] = useState(AUTO_SAVE_INTERVAL);
  
  const autoSaveTimer = useRef(null);

  // Carica bozza dal localStorage all'avvio
  useEffect(() => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        
        if (draft.customer) {
          setCustomer(draft.customer);
        }
        if (draft.orderItems) {
          setOrderItems(draft.orderItems);
        }
        if (draft.paymentMethod) {
          setPaymentMethod(draft.paymentMethod);
        }
      }
    } catch (error) {
      console.error('Errore caricamento bozza:', error);
      localStorage.removeItem(DRAFT_STORAGE_KEY);
    }
  }, []);

  // Funzione per salvare la bozza
  const saveDraft = useCallback((overrideData = {}) => {
    const currentCustomer = overrideData.customer || customer;
    const currentOrderItems = overrideData.orderItems || orderItems;
    const currentPaymentMethod = overrideData.paymentMethod || paymentMethod;

    try {
      const draft = {
        customer: currentCustomer,
        orderItems: currentOrderItems,
        paymentMethod: currentPaymentMethod,
        timestamp: Date.now()
      };

      // Salva solo se ci sono dati significativi
      if (currentCustomer?.companyName || currentOrderItems.length > 0) {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
        setLastSaved(draft.timestamp);
      }
    } catch (error) {
      console.error('Errore salvataggio bozza:', error);
    }
  }, [customer, orderItems, paymentMethod]);

  // Auto-save quando i dati cambiano
  useEffect(() => {
    if (autoSaveEnabled) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      
      autoSaveTimer.current = setTimeout(() => {
        saveDraft();
      }, autoSaveInterval);
    }

    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [customer, orderItems, paymentMethod, autoSaveEnabled, autoSaveInterval, saveDraft]);

  // Pulisce il timer quando il componente viene smontato
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, []);

  const updateCustomer = useCallback((newCustomer) => {
    setCustomer(newCustomer);
    // Salva immediatamente quando aggiorniamo il cliente
    setTimeout(() => saveDraft({ customer: newCustomer }), 100);
  }, [saveDraft]);

  const updateOrderItems = useCallback((newOrderItems) => {
    setOrderItems(newOrderItems);
    // Salva immediatamente quando aggiungiamo prodotti
    setTimeout(() => saveDraft({ orderItems: newOrderItems }), 100);
  }, [saveDraft]);

  const updatePaymentMethod = useCallback((newPaymentMethod) => {
    setPaymentMethod(newPaymentMethod);
    // Salva immediatamente quando aggiorniamo il metodo di pagamento
    setTimeout(() => saveDraft({ paymentMethod: newPaymentMethod }), 100);
  }, [saveDraft]);

  const clearDraft = useCallback(() => {
    setCustomer(null);
    setOrderItems([]);
    setPaymentMethod('contrassegno');
    setLastSaved(null);
    
    try {
      localStorage.removeItem(DRAFT_STORAGE_KEY);
      console.log('Bozza eliminata con successo');
    } catch (error) {
      console.error('Errore eliminazione bozza:', error);
    }
  }, []);

  const enableAutoSave = useCallback((interval = AUTO_SAVE_INTERVAL) => {
    setAutoSaveInterval(interval);
    setAutoSaveEnabled(true);
  }, []);

  const disableAutoSave = useCallback(() => {
    setAutoSaveEnabled(false);
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
  }, []);

  const saveDraftManually = useCallback(() => {
    saveDraft();
  }, [saveDraft]);

  // Statistiche bozza
  const draftStats = useMemo(() => ({
    hasCustomer: !!(customer?.companyName),
    itemCount: orderItems.length,
    totalItems: orderItems.reduce((sum, item) => sum + item.quantity, 0),
    totalValue: orderItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0),
    lastSaved,
    isAutoSaveEnabled: autoSaveEnabled
  }), [customer, orderItems, lastSaved, autoSaveEnabled]);

  return {
    customer,
    orderItems,
    paymentMethod,
    lastSaved,
    draftStats,
    updateCustomer,
    updateOrderItems,
    updatePaymentMethod,
    clearDraft,
    saveDraftManually,
    enableAutoSave,
    disableAutoSave
  };
};

export default useOrderDraft;